/**
 * What the Catalogue header says about the feed, read off the recorded sync
 * runs (CAE-1597).
 *
 * Before CAE-1597 the screen could only know what this session's own syncs had
 * returned, so the pill reset on every reload and there was no time to print.
 * The runs are recorded server-side now, so both survive a reload and show
 * syncs other operators ran.
 *
 * > **Only the fixture sync moves the pill.** It is the one sync with
 * > whole-feed scope: it asks Caesars about every enabled Sport, and a run
 * > where nothing got through is a feed that is gone. A market sync covers the
 * > fixtures it was given, and its `fetch_failed` cannot tell a dead feed from
 * > one stale event the feed has dropped, which is why the drill-down was
 * > already kept off the pill (CAE-1555). A market run that delivered is still
 * > evidence the feed answered, so it counts towards "last responded".
 */
import type { CatalogueSyncRuns } from '../../api/catalogue';

export interface FeedStatus {
  /** The newest fixture sync got nothing from the feed. */
  disconnected: boolean;
  /** When the newest fixture sync the feed answered finished: the "Last full sync". */
  lastFullSyncAt: string | null;
  /** The Sports the newest fixture sync failed for, when it was partial. */
  failedSports: string[];
  /** When the feed last answered any sync, fixtures or markets. */
  lastRespondedAt: string | null;
}

const later = (a: string | null, b: string | null): string | null => {
  if (!a) return b;
  if (!b) return a;
  return new Date(a).getTime() >= new Date(b).getTime() ? a : b;
};

export const feedStatus = (runs: CatalogueSyncRuns | undefined): FeedStatus => {
  const latest = runs?.fixtures.latest ?? null;
  return {
    disconnected: latest?.outcome === 'failed',
    lastFullSyncAt: runs?.fixtures.lastAnsweredAt ?? null,
    failedSports:
      latest?.outcome === 'partial'
        ? (latest.sportResults ?? [])
            .filter((sport) => sport.outcome !== 'succeeded')
            .map((sport) => sport.sportId)
        : [],
    lastRespondedAt: later(
      runs?.fixtures.lastAnsweredAt ?? null,
      runs?.markets.lastAnsweredAt ?? null,
    ),
  };
};

/** "just now", "2m ago", "3h ago", "4d ago". */
export const formatSyncAge = (iso: string, now: number = Date.now()): string => {
  const minutes = Math.max(0, Math.floor((now - new Date(iso).getTime()) / 60_000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
};
